import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';

import API from 'files/api.js';
import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import LottieView from 'views/Lottie.js';
import ProgressBar from 'views/ProgressBar.js';
import Utils from 'files/Utils.js';

export const AltBadge = ({ content, onClick, onMouseEnter, onMouseLeave, shortcut, style, utils }) => {

    const onBadgeClick = evt => {
        evt.stopPropagation();
        if(typeof(onClick) === 'function') {
            onClick();
        }
    }

    const onIconClick = (onClick, evt) => {
        evt.stopPropagation();
        onClick();
    }

    const getContent = () => {
        if(Array.isArray(content)) {
            return content.filter(({ color, icon, tag, text }) => {
                return icon || text ? true : false;
            }).map((props, index) => {
                if(props.icon) {
                    return (
                        <div
                        key={index}
                        className={props.onClick ? 'text-button' : ''}
                        onClick={props.onClick ? onIconClick.bind(this, props.onClick) : null}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 18,
                            height: 18,
                            marginLeft: index !== props.length - 1 ? 8 : 0
                        }}>
                            {Views.icon.right(props.icon)}
                        </div>
                    )
                }
                return (
                    <AltBadge
                    {...props}
                    key={index}
                    style={{
                        marginLeft: index !== props.length - 1 ? 8 : 0
                    }}
                    content={{
                        text: props.text,
                        color: props.color
                    }}/>
                )
            })
        }
        if(content.text && content.color) {
            return (
                <div
                className={onClick ? 'text-button' : ''}
                onClick={onClick ? onBadgeClick : null}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '3px 10px 3px 10px',
                    borderRadius: 20,
                    backgroundColor: Utils.hexToRGBA(content.color, 0.25),
                    ...style
                }}>
                    <span style={{
                        fontSize: 10,
                        fontWeight: 800,
                        color: window.theme === 'dark' ? 'white' : content.color,
                        whiteSpace: 'pre'
                    }}>{content.lowercase ? content.text : (content.text ? content.text.toString().toUpperCase() : '')}</span>
                </div>
            )
        }
        return null;
    }
    return getContent();
}

const getBorderRadius = props => {
    if(props.singleItem) {
        return {
            borderRadius: 10
        };
    }
    if(props.lastItem) {
        return {
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10
        }
    }
    if(props.firstItem) {
        return {
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
            borderBottom: `1px solid ${Appearance.colors.divider()}`
        }
    }
    return props.bottomBorder && {
        borderBottom: `1px solid ${Appearance.colors.divider()}`
    };
}

const Views = {
    icon: {
        remove: props => (
            <img
            className={'cursor-pointer'}
            onClick={props.onClick}
            src={'images/red-x-icon.png'}
            style={{
                background: `linear-gradient(90deg, ${Appearance.colors.textfield(0)}, ${Appearance.colors.textfield(0.9)}, ${Appearance.colors.textfield()})`,
                height: '100%',
                objectFit: 'contain',
                paddingLeft: 30,
                position: 'absolute',
                right: 12,
                width: 45,
                ...props.overlay === false && {
                    background: null,
                    height: 18,
                    position: 'relative',
                    paddingLeft: 0,
                    right: null,
                    marginLeft: 12,
                    width: 18,
                }
            }} />
        ),
        right: (type, onClick) => {
            let name = null;
            switch(type) {
                case 'arrow':
                name = 'next-arrow-light-grey.png';
                break;

                case 'arrow-white':
                name = 'next-arrow-white.png';
                break;

                case 'down-arrow':
                name = 'down-arrow-grey-small.png';
                break;

                case 'else-redirect':
                name = 'else-redirect-icon.png';
                break;

                case 'next-redirect':
                name = 'next-redirect-icon.png';
                break;

                case 'new':
                name = 'plus-icon-purple-small.png';
                break;

                case 'red-x':
                name = 'red-x-icon.png';
                break;

                case 'up-arrow':
                name = 'up-arrow-grey-small.png';
                break;

                case 'warning':
                name = 'warning-icon-yellow.png';
                break;

                default:
                return null;
            }
            return (
                <img
                src={`images/${name}`}
                className={onClick ? 'text-button' : null}
                onClick={onClick}
                style={{
                    width: 18,
                    height: 18,
                    objectFit: 'contain',
                    marginLeft: 8,
                    opacity: 1
                }} />
            )
        }
    },
    loader: props => {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                padding: 12
            }}>
                <LottieView
                loop={true}
                autoPlay={true}
                source={window.theme === 'dark' ? require('files/lottie/loading-dots-white.json') : require('files/lottie/loading-dots-grey.json')}
                style={{
                    width: 45,
                    height: 45
                }}/>
            </div>
        )
    },
    floating: (props = {}) => {
        let { insetLabel, offsetY = 0 } = props;
        return {
            position: 'absolute',
            top: offsetY + (insetLabel === false ? 40 : 47),
            left: 0,
            right: 0,
            backgroundColor: Appearance.colors.panel.background(),
            border: `1px solid ${Appearance.colors.border()}`,
            borderRadius: 8,
            boxShadow: '0 0.46875rem 1.1875rem rgba(0, 0, 0, 0.1)',
            zIndex: 1100,
            overflowX: 'hidden',
            overflowY: 'scroll',
            maxHeight: 250,
            marginBottom: 25
        }
    },
    entry: props => {

        let { badge, bottomBorder, bottomContent, containerStyle, hideIcon, icon, key, loading, onClick, prepend, propStyles, rightContent, rightIcon, style, subTitle, supportingTitle, textStyles, title } = props;

        const getChildComponents = () => {
            return (
                <>
                <div style={{
                    position: 'relative',
                    paddingLeft: 9,
                    paddingRight: 9,
                    paddingTop: 7,
                    paddingBottom: 7,
                    borderBottomWidth: bottomBorder === false ? 0 : 1,
                    borderBottomColor: Appearance.colors.divider(),
                    ...containerStyle
                }}>
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%',
                        alignItems: 'center'
                    }}>
                        {prepend}
                        {hideIcon !== true && (
                            typeof(icon) === 'function'
                                ?
                                props.icon()
                                :
                                <div
                                onClick={icon ? icon.onClick : null}
                                style={{
                                    position: 'relative'
                                }}>
                                    <div style={{
                                        ...Appearance.icons.standard(),
                                        overflow: 'hidden',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: null,
                                        ...icon && icon.style
                                    }}>
                                        <img
                                        src={icon && icon.path ? icon.path : `images/logo-icon-${window.theme}.png`}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            backgroundColor: !icon || icon.path.includes('clear') || icon.path.includes('default') ? Appearance.colors.primary() : null,
                                            ...icon && icon.imageStyle
                                        }}/>
                                    </div>
                                    {icon && icon.badge && (
                                        <div style={{
                                            width: 20,
                                            height: 20,
                                            minWidth: 20,
                                            minHeight: 20,
                                            paddingLeft: 5,
                                            paddingRight: 5,
                                            borderRadius: 10,
                                            overflow: 'hidden',
                                            position: 'absolute',
                                            top: -2,
                                            right: -6,
                                            textAlign: 'center',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: typeof(icon.badge) === 'object' && icon.badge.color ? icon.badge.color : Appearance.colors.primary()
                                        }}>
                                            {typeof(icon.badge) === 'object' && icon.badge.text
                                                ?
                                                <span style={{
                                                    width: '100%',
                                                    fontSize: 10,
                                                    fontWeight: 900,
                                                    color: 'white'
                                                }}>{icon.badge.text}</span>
                                                :
                                                <div style={{
                                                    width: 5,
                                                    height: 5,
                                                    backgroundColor: 'white',
                                                    borderRadius: 2.5,
                                                    overflow: 'hidden'
                                                }}/>
                                            }
                                        </div>
                                    )}
                                </div>
                        )}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            paddingLeft: hideIcon === true ? 2 : 12,
                            flexShrink: 1,
                            width: '100%',
                            minWidth: 0
                        }}>
                            {typeof(title) === 'string' && (
                                <span
                                {...propStyles && propStyles.title}
                                style={{
                                    ...Appearance.textStyles.title(),
                                    ...textStyles && textStyles.title,
                                    flexGrow: 1,
                                    lineHeight: 1.25
                                }}>{title}</span>
                            )}
                            {typeof(title) !== 'string' && (
                                <div
                                {...propStyles && propStyles.title}
                                style={{
                                    flexGrow: 1,
                                    lineHeight: 1.25
                                }}>{title}</div>
                            )}
                            {typeof(subTitle) === 'string' && (
                                <span
                                {...propStyles && propStyles.subTitle}
                                style={{
                                    ...Appearance.textStyles.subTitle(),
                                    ...textStyles && textStyles.subTitle,
                                    lineHeight: 1.25,
                                    marginTop: 2
                                }}>{subTitle}</span>
                            )}
                            {typeof(supportingTitle) === 'string' && (
                                <span
                                {...propStyles && propStyles.supportingTitle}
                                style={{
                                    ...Appearance.textStyles.subTitle(),
                                    ...textStyles && textStyles.supportingTitle,
                                    marginTop: 2
                                }}>{supportingTitle}</span>
                            )}
                        </div>
                        {rightIcon && (
                            <img
                            src={rightIcon.path}
                            style={{
                                ...rightIcon.style,
                                minWidth: 25,
                                minHeight: 25,
                                width: 25,
                                height: 25
                            }}/>
                        )}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            flexShrink: 1
                        }}>
                            {badge && (
                                <AltBadge
                                content={badge}
                                onClick={badge.onClick} />
                            )}
                            {rightContent || (onClick && Views.icon.right('arrow'))}
                        </div>
                    </div>
                    {loading === true && (
                        <div style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 2,
                            borderRadius: 1,
                            overflow: 'hidden'
                        }}>
                            <ProgressBar />
                        </div>
                    )}
                </div>
                {bottomContent}
                </>
            )
        }

        const getContent = () => {
            return (
                <div
                key={key}
                className={onClick ? `view-entry ${window.theme}` : ''}
                onClick={onClick}
                style={{
                    overflow: 'hidden',
                    ...getBorderRadius(props),
                    ...style
                }}>
                    {getChildComponents()}
                </div>
            )
        }
        return getContent();
    },
    row: ({ bottomBorder, key, label, nullValue, onClick, style, value }) => {
        return (
            <div
            key={key}
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px 8px 12px',
                borderBottom: bottomBorder === false ? null : `1px solid ${Appearance.colors.divider()}`,
                ...style
            }}>
                <span style={{
                    ...Appearance.textStyles.key(),
                    whiteSpace: 'normal'
                }}>{label}</span>
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center'
                }}>
                    <span style={{
                        ...Appearance.textStyles.value(),
                        ...style && style.value
                    }}>{value !== null && value !== undefined ? value : (nullValue || 'Not Added')}</span>
                    {typeof(onClick) === 'function' && (
                        <img
                        src={'images/next-arrow-light-grey.png'}
                        style={{
                            width: 15,
                            height: 15,
                            objectFit: 'contain',
                            marginLeft: 8,
                            opacity: 1
                        }} />
                    )}
                </div>
            </div>
        )
    }
}

export default Views;

export const AsyncViewComponent = props => {

    const [loading, setLoading] = useState(loading);

    const onItemClick = async () => {
        try {
            setLoading(true);
            await props.onClick();
            setLoading(false);
        } catch(e) {
            console.error(e.message);
        }
    }

    return (
        Views.entry({
            ...props,
            loading: loading,
            onClick: onItemClick
        })
    )
}

export const ContentContainer = ({ children, initVal = 'init', loading, props }) => {

    const getContent = () => {
        if(loading === initVal) {
            return Views.loader();
        }
        if(!children || children.length === 0) {
            return (
                Views.entry({
                    ...props,
                    hideIcon: true,
                    bottomBorder: false
                })
            )
        }
        return children.map((props, index) => (
            Views.entry({
                ...props,
                firstItem: index === 0,
                singleItem: children.length === 1,
                lastItem: index === children.length - 1,
                bottomBorder: index !== children.length - 1,
            })
        ));
    }
    return getContent();
}

export const ViewComponent = props => {
    return Views.entry({
        ...props,
        key: undefined
    });
}
